import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate, SafeAnchor } from '@hydra/common/components';
import { useReviewContext } from '@hydra/common/context';
import { CriteriumTable, CriteriumSkeleton } from '@hydra/common/criterium';

const useStyles = makeStyles(theme => ({
  image: {
    maxWidth: '100%'
  },
  addSocialLink: {
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    marginTop: theme.spacing(2)
  },
  row: {
    display: 'flex',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1)
  },
  rowLeft: {
    display: 'inline-block',
    width: '25%',
    fontWeight: theme.typography.fontWeightSemibold
  },
  rowRight: {
    display: 'inline-block',
    width: '75%'
  }
}));

export const CriteriumSocialSection = ({ image, sections, isDetected, network, username, urlLinked, confidence, urls }) => {
  const classes = useStyles();
  const { isPdf, variant } = useReviewContext();
  const addYourSocialAccount = (
    <div className={classes.addSocialLink}>
      {variant === 'nocrit' ? <CriteriumSkeleton width='40%' /> : <Translate component='div' content={`add_${network.toLowerCase()}_or_create_title`} />}
    </div>
  );

  if (typeof confidence === 'number' && confidence !== 1) {
    if (urls.length === 0) {
      return (
        <>
          {variant === 'nocrit' ? <CriteriumSkeleton /> : <Translate component='i' content='not_found' />}
          {!isPdf && addYourSocialAccount}
        </>
      );
    } else if (urls.length === 1) {
      return (
        <>
          {
            variant === 'nocrit'
              ? <CriteriumSkeleton />
              : <Translate
                  component='p'
                  content='social_profile_not_in_structured_data_with_link' with={{ url: urls[0] }}
                />
          }
          {!isPdf && addYourSocialAccount}
        </>
      );
    } else if (urls.length > 1) {
      const rows = urls.map(url => [<SafeAnchor href={url} rel='nofollow noopener noreferrer' target='_blank' key={url}>{url}</SafeAnchor>]);
      return (
        <>
          <Translate
            component='p'
            content='social_profiles_not_in_structured_data'
            with={{ network, count: urls.length }}
          />
          {!isPdf && addYourSocialAccount}
          <CriteriumTable
            head={['']}
            rows={rows}
            thinHead
            nowrap
          />
        </>
      );
    }
  }

  return (
    <>
      {isDetected
        ? (
          <>
            {
              variant === 'nocrit'
                ? <CriteriumSkeleton variant='rect' width='10%' style={{ height: '100px' }} />
                : (image ? <img className={classes.image} src={image} /> : <Icon type={network.toLowerCase()} size='lg' />)
            }

            {urlLinked && (variant === 'nocrit' ? <CriteriumSkeleton width='75%' /> : <Translate component='div' content={`${network.toLowerCase()}_account_linked`} with={{ name: username }} />)}

            {sections
              .filter(({ node }) => node !== null && node !== undefined)
              .map(({ name, node }, index) => (
                <div key={name} className={classes.row}>
                  <div className={classes.rowLeft}>
                    <Translate content={name} />
                  </div>
                  <div className={classes.rowRight}>
                    {
                      variant === 'nocrit'
                        ? <CriteriumSkeleton width={index % 2 === 0 ? '60%' : '40%'} />
                        : (
                            index === 0
                              ? (
                                <>
                                  <SafeAnchor
                                    href={node}
                                    rel='nofollow noopener noreferrer'
                                    target='_blank'
                                  >
                                    {node}
                                  </SafeAnchor>
                                  {!isPdf && <Translate component='div' content='change_account' />}
                                </>
                                )
                              : node
                          )
                    }
                  </div>
                </div>
              ))}
          </>
          )
        : (
          <>
            {variant === 'nocrit' ? <CriteriumSkeleton width='70%' /> : <Translate component='i' content='not_found' />}
            {!isPdf && addYourSocialAccount}
          </>
          )}
    </>
  );
};

CriteriumSocialSection.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, node: PropTypes.node })
  ),
  isDetected: PropTypes.bool.isRequired,
  network: PropTypes.string.isRequired,
  image: PropTypes.string,
  username: PropTypes.string,
  urlLinked: PropTypes.bool,
  confidence: PropTypes.number,
  urls: PropTypes.array
};

export default CriteriumSocialSection;
